import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  role: string;
}

interface AuthContextType {
  user: AdminUser | null;
  token: string | null;
  /** Epoch-ms timestamp of the current token's expiry. Null when logged out. */
  expiresAt: number | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  /** Subscribe to "session expired" callbacks. Called when the active
   *  token crosses its exp timestamp while the user is on an authed
   *  surface. Mount the returned cleanup in a useEffect. */
  onExpired: (cb: () => void) => () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const TOKEN_KEY = "merryflix_admin_token";
const USER_KEY = "merryflix_admin_user";
/**
 * Default session lifetime when the token doesn't expose a `exp` claim.
 * Matches the backend's admin login TTL — admin tokens reuse the same
 * 30-minute `ACCESS_TOKEN_TTL_SECONDS`.
 */
const DEFAULT_TOKEN_TTL_MS = 30 * 60 * 1000;

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}

/**
 * Decode the JWT payload (without verifying signature — the server
 * is the source of truth) and return the `exp` claim as epoch-ms, or
 * null if we can't read one. Used by the expiry watchdog so we never
 * rely solely on the backend response.
 */
function decodeJwtExpiry(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const json = atob(padded);
    const payload = JSON.parse(json) as { exp?: number };
    if (typeof payload.exp === "number") return payload.exp * 1000;
    return null;
  } catch {
    return null;
  }
}

function computeExpiry(token: string, fallbackMs: number): number {
  const jwtMs = decodeJwtExpiry(token);
  if (jwtMs) return jwtMs;
  return Date.now() + fallbackMs;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Subscribers that want to know when the active session expires.
   * Lives in a ref so adding/removing a subscriber doesn't trigger
   * a re-render.
   */
  const expiryListenersRef = useRef<Set<() => void>>(new Set());

  // Initial bootstrap: read token from storage, decode expiry.
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);

    if (storedToken && storedUser) {
      setToken(storedToken);
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        localStorage.removeItem(USER_KEY);
      }
      // Compute expiry from JWT payload — falling back to the default
      // TTL if the claim can't be decoded.
      const exp = computeExpiry(storedToken, DEFAULT_TOKEN_TTL_MS);
      if (exp <= Date.now()) {
        // Already expired — clear everything and tell subscribers.
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setToken(null);
        setUser(null);
        setExpiresAt(null);
        // Fire on the next tick so the navigation effect can settle
        // before any redirect callback runs.
        const handle = window.setTimeout(() => {
          expiryListenersRef.current.forEach((fn) => fn());
        }, 0);
        return () => window.clearTimeout(handle);
      }
      setExpiresAt(exp);
    }
    setIsLoading(false);
  }, []);

  /**
   * Schedules an automatic logout when the active token crosses its exp.
   * Cleared whenever the token changes (login / logout / refresh).
   */
  useEffect(() => {
    if (!expiresAt || !token) return;
    const ms = expiresAt - Date.now();
    if (ms <= 0) {
      const handle = window.setTimeout(() => {
        expiryListenersRef.current.forEach((fn) => fn());
      }, 0);
      return () => window.clearTimeout(handle);
    }
    const handle = window.setTimeout(() => {
      // Notify subscribers FIRST so they can redirect synchronously,
      // THEN clear local state.
      expiryListenersRef.current.forEach((fn) => fn());
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setToken(null);
      setUser(null);
      setExpiresAt(null);
    }, ms);
    return () => window.clearTimeout(handle);
  }, [expiresAt, token]);

  const login = async (email: string, password: string) => {
    const res = await fetch(
      `${import.meta.env.VITE_ADMIN_API_URL || "http://localhost:3001/api"}/auth/admin/login`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      }
    );

    if (!res.ok) {
      const err = await res.text();
      throw new Error(err || "Login failed");
    }

    const data = await res.json();

    if (!data.success) {
      throw new Error(data.error || "Login failed");
    }

    const { token: newToken, user: newUser } = data;

    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));

    setToken(newToken);
    setUser(newUser);
    setExpiresAt(computeExpiry(newToken, DEFAULT_TOKEN_TTL_MS));
  };

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
    setExpiresAt(null);
  }, []);

  const onExpired = useCallback((cb: () => void) => {
    expiryListenersRef.current.add(cb);
    return () => {
      expiryListenersRef.current.delete(cb);
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        expiresAt,
        login,
        logout,
        isLoading,
        onExpired,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
